// UserProfileModal.js
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../supabaseClient';
import { useCoin } from '../CoinContext';
import { fetchUserByWallet } from './userUtils';

function UserProfileModal({ isOpen, onClose }) {
  const { walletAddress, currentPoints, setNickname } = useCoin();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [nicknameError, setNicknameError] = useState('');
  const [savingNickname, setSavingNickname] = useState(false);

  // 프로필 이미지 관련 상태
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // 캐릭터 선택 모달 상태 (가장 상위 레이어)
  const [isCharacterModalOpen, setIsCharacterModalOpen] = useState(false);
  const [characterLoading, setCharacterLoading] = useState(false);
  const [characterError, setCharacterError] = useState('');
  const [characters, setCharacters] = useState([]);

  useEffect(() => {
    if (isOpen && walletAddress) {
      fetchUserData();
      setIsEditingNickname(false);
      setNicknameError('');
    }
  }, [isOpen, walletAddress]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const data = await fetchUserByWallet(); // Edge Function 사용
      if (data) {
        setUserData(data);
        setNewNickname(data.nickname || '');
        setProfileImageUrl(data.profile || null);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
    setLoading(false);
  };

  const handleEditNickname = () => {
    setIsEditingNickname(true);
    setNewNickname(userData?.nickname || '');
    setNicknameError('');
  };

  const handleCancelEdit = () => {
    setIsEditingNickname(false);
    setNewNickname(userData?.nickname || '');
    setNicknameError('');
  };

  const handleSaveNickname = async () => {
    if (!newNickname.trim()) {
      setNicknameError('Please enter a nickname');
      return;
    }

    if (newNickname.length < 2 || newNickname.length > 20) {
      setNicknameError('Nickname must be 2-20 characters');
      return;
    }

    const nicknameRegex = /^[가-힣a-zA-Z0-9_]+$/;
    if (!nicknameRegex.test(newNickname)) {
      setNicknameError('Only Korean, English, numbers, and _ allowed');
      return;
    }

    setSavingNickname(true);
    setNicknameError('');

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setNicknameError('Please login again');
        setSavingNickname(false);
        return;
      }

      const response = await supabase.functions.invoke('update-nickname', {
        body: { nickname: newNickname },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        if (response.error.message?.includes('already taken')) {
          setNicknameError('This nickname is already taken');
        } else {
          setNicknameError('Failed to change nickname');
        }
        setSavingNickname(false);
        return;
      }

      setUserData({ ...userData, nickname: newNickname });
      setNickname(newNickname);
      setIsEditingNickname(false);

      alert('Nickname changed successfully!');
    } catch (error) {
      console.error('Failed to change nickname:', error);
      setNicknameError('Failed to change nickname');
    }

    setSavingNickname(false);
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 현재 프로필 이미지 직접 업로드는 잠시 비활성화 (캐릭터 선택으로 변경)
  const handleProfileImageClick = async () => {
    if (!walletAddress) return;

    setCharacterError('');
    setCharacters([]);
    setIsCharacterModalOpen(true);
    setCharacterLoading(true);

    try {
      // 세션에서 액세스 토큰 가져오기 (update-nickname과 동일 패턴)
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setCharacterError('로그인이 필요합니다.');
        setCharacterLoading(false);
        return;
      }

      // 엣지 펑션 getUserCharacters 호출 (지갑 주소 전달 + Authorization 헤더)
      const { data, error } = await supabase.functions.invoke(
        'getUserCharacters',
        {
          body: { userId: walletAddress },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (error) {
        console.error(error);
        setCharacterError('캐릭터 정보를 불러오지 못했습니다.');
      } else {
        // Edge Function 응답: { success, data: { characters: [...], paginationResult } }
        // 엣지 함수 응답 구조를 방어적으로 처리
const payload = data || {};
// 1순위: data.data.characters (현재 구조)
const charactersFromApi =
  payload?.data?.characters ||
  // 혹시 나중에 data.data.data.characters 로 바뀌는 경우까지 대비
  payload?.data?.data?.characters ||
  [];
        console.log(charactersFromApi);
        const mapped = charactersFromApi.map((c) => ({
  id: c.assetKey || c.tokenId || c.name,
  name: c.name,
  level: c.data?.level,
  jobCode: c.data?.jobCode,
  world: c.data?.world,
  imageUrl: c.data?.imageUrl,
}));
        setCharacters(mapped);
      }
    } catch (err) {
      console.error(err);
      setCharacterError('캐릭터 정보를 불러오지 못했습니다.');
    } finally {
      setCharacterLoading(false);
    }
  };

  // 캐릭터 선택 시: set-profile 엣지 함수 호출 후 모달 닫기
const handleSelectCharacter = async (character) => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      alert('로그인이 필요합니다.');
      return;
    }

    // set-profile 엣지 펑션 호출 (캐릭터 이미지 URL 전송)
    const { data, error } = await supabase.functions.invoke('set-profile', {
      body: {
        profileImageUrl: character.imageUrl,
        characterName: character.name,
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('set-profile error:', error);
      alert('프로필 설정에 실패했습니다.');
      return;
    }

    // 프론트 상태도 바로 업데이트해서 모달 닫은 뒤에도 반영
    if (character.imageUrl) {
      setProfileImageUrl(character.imageUrl);
    }

    console.log('selected character:', character, 'set-profile result:', data);
    setIsCharacterModalOpen(false);
  } catch (e) {
    console.error('set-profile exception:', e);
    alert('프로필 설정 중 오류가 발생했습니다.');
  }
};

  const handleCloseCharacterModal = () => {
    setIsCharacterModalOpen(false);
  };

  // Render with Portal
  return (
    <>
      {ReactDOM.createPortal(
        <div
          onClick={handleBackdropClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: 16,
              padding: '24px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              position: 'relative',
            }}
          >
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px 8px',
                lineHeight: 1,
              }}
            >
              ×
            </button>

            <h2
              style={{
                margin: '0 0 20px 0',
                fontSize: '20px',
                fontWeight: 600,
                color: '#333',
              }}
            >
              User Profile
            </h2>

            {loading ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 0',
                  color: '#666',
                }}
              >
                Loading...
              </div>
            ) : userData ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                {/* 프로필 이미지 영역 (클릭 시 캐릭터 선택 모달 열기) */}
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <div
  onClick={handleProfileImageClick}
  style={{
    width: 96,
    height: 96,
    borderRadius: '50%',
    overflow: 'hidden',
    margin: '0 auto 8px',
    border: '2px solid #e0e0e0',
    cursor: 'pointer',
    position: 'relative',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  {profileImageUrl ? (
    <img
      src={profileImageUrl}
      alt="Profile"
      style={{
        width: '150%',      // 원보다 약간 크게
        height: '150%',
        objectFit: 'contain',
        objectPosition: 'center center', // 중앙 정렬
        transform: 'translateY(-8px)',    // 살짝 아래로 내려서 캐릭터 중심 맞추기
      }}
    />
  ) : (
    <span
      style={{
        fontSize: '12px',
        color: '#888',
      }}
    >
      Select Character
    </span>
  )}
  <div
    style={{
      position: 'absolute',
      bottom: 0,
      width: '100%',
      padding: '4px 0',
      background:
        'linear-gradient(to top, rgba(0,0,0,0.45), transparent)',
      color: '#fff',
      fontSize: '10px',
      textAlign: 'center',
    }}
  >
    Change
  </div>
</div>
                  {/* file input 유지 (현재는 사용 안 함) */}
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={() => {}}
                    style={{ display: 'none' }}
                  />
                  {uploadingImage && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Uploading...
                    </div>
                  )}
                </div>

                {/* 닉네임 */}
                <div style={infoRowStyle}>
                  <span style={labelStyle}>Nickname</span>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {isEditingNickname ? (
                      <>
                        <input
                          type="text"
                          value={newNickname}
                          onChange={(e) => setNewNickname(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '6px 10px',
                            border: '1px solid #ddd',
                            borderRadius: 6,
                            fontSize: '14px',
                            outline: 'none',
                            maxWidth: '200px',
                          }}
                          placeholder="Enter nickname"
                          maxLength={20}
                          disabled={savingNickname}
                        />
                        <button
                          onClick={handleSaveNickname}
                          disabled={savingNickname}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 6,
                            background: '#1976d2',
                            color: '#fff',
                            border: 'none',
                            cursor: savingNickname
                              ? 'not-allowed'
                              : 'pointer',
                            fontSize: '12px',
                            fontWeight: 600,
                            opacity: savingNickname ? 0.6 : 1,
                          }}
                        >
                          {savingNickname ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={savingNickname}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 6,
                            background: '#999',
                            color: '#fff',
                            border: 'none',
                            cursor: savingNickname
                              ? 'not-allowed'
                              : 'pointer',
                            fontSize: '12px',
                            fontWeight: 600,
                            opacity: savingNickname ? 0.6 : 1,
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={valueStyle}>
                          {userData.nickname || '-'}
                        </span>
                        <button
                          onClick={handleEditNickname}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#666',
                            fontSize: '16px',
                          }}
                          title="Change nickname"
                        >
                          ✏️
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {nicknameError && (
                  <div
                    style={{
                      color: '#d32f2f',
                      fontSize: '12px',
                      marginTop: '-8px',
                      textAlign: 'right',
                    }}
                  >
                    {nicknameError}
                  </div>
                )}

                {/* 지갑 주소 */}
                <div style={infoRowStyle}>
                  <span style={labelStyle}>Wallet Address</span>
                  <span
                    style={{
                      ...valueStyle,
                      wordBreak: 'break-all',
                      fontSize: '12px',
                    }}
                  >
                    {userData.wallet_address}
                  </span>
                </div>

                <div
                  style={{
                    height: '1px',
                    background: '#e0e0e0',
                    margin: '8px 0',
                  }}
                />

                {/* 포인트 관련 정보 */}
                <div style={infoRowStyle}>
                  <span style={labelStyle}>Points Per Second</span>
                  <span
                    style={{
                      ...valueStyle,
                      color: '#1976d2',
                      fontWeight: 600,
                    }}
                  >
                    {userData.per_sec}
                  </span>
                </div>

                <div style={infoRowStyle}>
                  <span style={labelStyle}>Current Points</span>
                  <span
                    style={{
                      ...valueStyle,
                      color: '#1976d2',
                      fontWeight: 600,
                    }}
                  >
                    {currentPoints.toLocaleString()}
                  </span>
                </div>

                <div style={infoRowStyle}>
                  <span style={labelStyle}>Total Points</span>
                  <span style={valueStyle}>
                    {(userData.total_points || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 0',
                  color: '#666',
                }}
              >
                Unable to load user information.
              </div>
            )}

            <div
              style={{ marginTop: '24px', display: 'flex', gap: '8px' }}
            >
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 8,
                  background: '#1976d2',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 캐릭터 선택 모달: 최상위 레이어 (기존 모달보다 zIndex 더 높게) */}
      {isCharacterModalOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
            }}
          >
            <div
              style={{
                backgroundColor: '#111827',
                borderRadius: 16,
                padding: '20px',
                width: '100%',
                maxWidth: 640,
                maxHeight: '80vh',
                overflowY: 'auto',
                color: '#e5e7eb',
                boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                position: 'relative',
              }}
            >
              <button
                onClick={handleCloseCharacterModal}
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  background: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  fontSize: 22,
                }}
              >
                ×
              </button>

              <h3
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                캐릭터 선택
              </h3>
              <p
                style={{
                  margin: 0,
                  marginBottom: 16,
                  fontSize: 13,
                  color: '#9ca3af',
                }}
              >
                프로필 사진으로 사용할 캐릭터를 선택하세요.
              </p>

              {characterLoading ? (
  <div
    style={{
      padding: '40px 0',
      textAlign: 'center',
      color: '#9ca3af',
    }}
  >
    불러오는 중...
  </div>
) : characterError ? (
  <div
    style={{
      padding: '24px 0',
      textAlign: 'center',
      color: '#fca5a5',
      fontSize: 13,
    }}
  >
    {characterError}
  </div>
) : characters.length === 0 ? (
  <div
    style={{
      padding: '24px 0',
      textAlign: 'center',
      color: '#9ca3af',
      fontSize: 13,
    }}
  >
    표시할 캐릭터가 없습니다.
  </div>
) : (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: 16,
      marginTop: 8,
    }}
  >
    {characters.map((ch) => (
      <button
        key={ch.id || ch.characterId || ch.name}
        onClick={() => handleSelectCharacter(ch)}
        style={{
          backgroundColor: '#020617',
          borderRadius: 18,
          border: '1px solid #1f2937',
          padding: 14,
          cursor: 'pointer',
          textAlign: 'center',
          color: '#e5e7eb',
          fontSize: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          transition:
            'transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease, background-color 0.12s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow =
            '0 14px 30px rgba(0,0,0,0.55)';
          e.currentTarget.style.borderColor = '#4b5563';
          e.currentTarget.style.backgroundColor = '#020617';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.borderColor = '#1f2937';
          e.currentTarget.style.backgroundColor = '#020617';
        }}
      >
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 20,
            margin: '0 auto',
            overflow: 'hidden',
            background:
              'radial-gradient(circle at 30% 0%, #1f2937, #020617 60%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {ch.imageUrl ? (
            <img
              src={ch.imageUrl}
              alt={ch.name || 'character'}
              style={{
                        width: '150%',
                        height: '150%',
        objectFit: 'contain',
        objectPosition: 'center center', // 중앙 정렬
        transform: 'translateY(-8px)',    // 살짝 아래로 내려서 캐릭터 중심 맞추기
              }}
            />
          ) : (
            <span
              style={{
                fontSize: 13,
                color: '#6b7280',
              }}
            >
              {ch.name || ch.characterName || '캐릭터'}
            </span>
          )}
        </div>
      </button>
    ))}
  </div>
)}

<div
  style={{
    marginTop: 20,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  }}
>
  <button
    onClick={handleCloseCharacterModal}
    style={{
      padding: '8px 14px',
      borderRadius: 8,
      backgroundColor: '#374151',
      color: '#e5e7eb',
      border: 'none',
      fontSize: 13,
      cursor: 'pointer',
    }}
  >
    닫기
  </button>
</div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

const infoRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '8px 0',
};

const labelStyle = {
  fontSize: '14px',
  color: '#666',
  fontWeight: 500,
  minWidth: '100px',
};

const valueStyle = {
  fontSize: '14px',
  color: '#333',
  fontWeight: 400,
  textAlign: 'right',
  flex: 1,
};

export default UserProfileModal;
